import { bucketData, BucketedDataItem } from 'utils/timeseries/bucket';

import mean from 'lodash/mean';
import { getItemTime } from 'utils/timeseries/timeSeriesUtils';
import sortBy from 'lodash/sortBy';
import { applyShowDataFor } from 'utils/components/ShowDataFor/ShowDataFor';
import { SignalTileConfigState } from 'widgets/signalTiles/signalTile/state/config/SignalTileConfigState';
import {
    SignalTileDataState,
    SignalTileTimeElement,
} from 'widgets/signalTiles/signalTile/state/stream/SignalTileStreamState';

export interface SignalBucket extends BucketedDataItem {
    value?: number;
}

export interface SignalChartTimeSeriesItem extends BucketedDataItem {
    time: number;
    value?: number;
    above?: number;
    middle?: number;
    below?: number;
}

export interface SignalTileChartData {
    startTime?: number;
    endTime?: number;
    timeSeries: SignalChartTimeSeriesItem[];
}

export function buildSignalTileChartData(
    upperBoundary: number,
    lowerBoundary: number,
    config: SignalTileConfigState,
    data?: SignalTileDataState
): SignalTileChartData | undefined {
    const buckets = bucketSignalData(config, data);

    if (!buckets) {
        return;
    }

    let timeSeries = mapBucketsToTimeSeries(
        upperBoundary,
        lowerBoundary,
        buckets
    );

    const times = data!.timeSeries.map((d) => d._i).sort();
    const startTime = times[0] * 60000;
    const startItem = timeSeries.find((t) => t.time === startTime);
    if (!startItem) {
        timeSeries = [
            {
                time: startTime,
                startTime,
                midTime: startTime,
                endTime: startTime,
                endTimeDisplay: startTime,
            },
            ...timeSeries,
        ];
    }

    return {
        timeSeries,
        startTime: startTime,
        endTime: timeSeries[timeSeries.length - 1].time,
    };
}

function bucketSignalData(
    config: SignalTileConfigState,
    data?: SignalTileDataState
): SignalBucket[] | undefined {
    const { bucketSize, numWindows, showDataFor } = config;

    if (!data?.timeSeries?.length) {
        return undefined;
    }

    const withoutLookbackPeriod = sortBy(data.timeSeries, '_i').slice(
        numWindows
    );

    const filteredData = applyShowDataFor(withoutLookbackPeriod, showDataFor);

    if (filteredData.length === 0) {
        return undefined;
    }

    const sorted = filteredData.sort((a, b) => (a._i > b._i ? +1 : -1));

    if (bucketSize === 1) {
        return sorted.map((d) => ({
            startTime: getItemTime(d),
            midTime: getItemTime(d),
            endTime: getItemTime(d),
            value: d.value,
            endTimeDisplay: getItemTime(d),
        }));
    }
    const calculateBucketedValues = (
        bucket: SignalBucket,
        items: SignalTileTimeElement[]
    ) => {
        const values = items.map((i) => i.value).filter((i) => i !== undefined);
        const value = mean(values);
        bucket.value = value;
    };

    return bucketData<SignalTileTimeElement, SignalBucket>(
        bucketSize,
        sorted,
        [],
        calculateBucketedValues,
        undefined,
        true
    );
}

function mapBucketsToTimeSeries(
    upperBoundary: number,
    lowerBoundary: number,
    buckets: SignalBucket[]
): SignalChartTimeSeriesItem[] {
    const ret: SignalChartTimeSeriesItem[] = [];

    let previous: SignalChartTimeSeriesItem | undefined;

    for (const bucket of buckets) {
        const item = mapBucket(upperBoundary, lowerBoundary, bucket);
        if (item === undefined) {
            continue;
        }

        fillBoundaryGap(upperBoundary, lowerBoundary, ret, item, previous);

        ret.push(item);

        previous = item;
    }

    return ret;
}

function mapBucket(
    upperBoundary: number,
    lowerBoundary: number,
    bucket: SignalBucket
): SignalChartTimeSeriesItem | undefined {
    if (bucket.value === undefined) {
        return undefined;
    }

    const ret: SignalChartTimeSeriesItem = {
        time: bucket.midTime,
        value: bucket.value,
        startTime: bucket.startTime,
        endTime: bucket.endTime,
        midTime: bucket.midTime,
        endTimeDisplay: bucket.endTimeDisplay,
    };

    if (bucket.value > upperBoundary) {
        ret.above = bucket.value;
    } else if (bucket.value < lowerBoundary) {
        ret.below = bucket.value;
    } else {
        ret.middle = bucket.value;
    }

    return ret;
}

function fillBoundaryGap(
    upperBoundary: number,
    lowerBoundary: number,
    items: SignalChartTimeSeriesItem[],
    current: SignalChartTimeSeriesItem,
    previous: SignalChartTimeSeriesItem | undefined
) {
    if (!previous) {
        return;
    }

    const buildFiller: (
        curr: SignalChartTimeSeriesItem,
        prev: SignalChartTimeSeriesItem
    ) => SignalChartTimeSeriesItem = (curr, prev) => {
        const fillerTime = curr.time - (curr.time - prev.time) / 2;
        return {
            time: fillerTime,
            value: 0,
            startTime: fillerTime,
            endTime: fillerTime,
            midTime: fillerTime,
            endTimeDisplay: fillerTime,
        };
    };

    if (previous.above !== undefined && current.above === undefined) {
        const filler = buildFiller(current, previous);
        filler.above = upperBoundary;
        if (current.middle) {
            filler.middle = upperBoundary;
            filler.value = inBetween(previous.above, current.middle);
        }
        if (current.below) {
            filler.below = lowerBoundary;
            filler.value = inBetween(previous.above, current.below);
        }
        items.push(filler);
    }

    if (previous.middle !== undefined && current.middle === undefined) {
        const filler = buildFiller(current, previous);

        if (current.above) {
            filler.middle = upperBoundary;
            filler.above = upperBoundary;
            filler.value = inBetween(current.above, previous.middle);
        }
        if (current.below) {
            filler.middle = lowerBoundary;
            filler.below = lowerBoundary;
            filler.value = inBetween(previous.middle, current.below);
        }
        items.push(filler);
    }

    if (previous.below !== undefined && current.below === undefined) {
        const filler = buildFiller(current, previous);
        filler.below = lowerBoundary;
        if (current.middle) {
            filler.middle = lowerBoundary;
            filler.value = inBetween(current.middle, previous.below);
        }
        if (current.above) {
            filler.above = upperBoundary;
            filler.value = inBetween(current.above, previous.below);
        }
        items.push(filler);
    }
}

function inBetween(high: number, low: number) {
    return high - (high - low) / 2;
}
